import React, { useState } from 'react';
import {
  SimpleForm,
  TextInput,
  NumberInput,
  SelectInput,
  DateInput,
  required,
  useNotify,
  useRefresh,
  useDataProvider,
  Toolbar,
  SaveButton,
  useTranslate,
  useGetOne,
} from 'react-admin';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  IconButton,
  Grid,
  Box,
  Button,
  Typography,
  Chip,
  Divider,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import PersonIcon from '@mui/icons-material/Person';
import ReceiptIcon from '@mui/icons-material/Receipt';

const PaymentEditToolbar = ({ onDelete }: { onDelete?: () => void }) => {
  const translate = useTranslate();

  return (
    <Toolbar
      sx={{
        display: 'flex',
        justifyContent: 'space-between',
        padding: '16px 24px',
        borderTop: '1px solid #e0e0e0',
      }}
    >
      <Box>
        {onDelete && (
          <Button onClick={onDelete} color="error" variant="outlined" sx={{ mr: 1 }}>
            {translate('ra.action.delete')}
          </Button>
        )}
      </Box>
      <SaveButton label={translate('ra.action.save')} />
    </Toolbar>
  );
};

interface PaymentEditDialogProps {
  open: boolean;
  onClose: () => void;
  record: any;
}

export const PaymentEditDialog: React.FC<PaymentEditDialogProps> = ({
  open,
  onClose,
  record,
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const dataProvider = useDataProvider();
  const translate = useTranslate();
  const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

  // Get customer and invoice data for display
  const { data: customer } = useGetOne(
    'customers',
    { id: record?.customerId },
    { enabled: !!record?.customerId }
  );

  const { data: invoice } = useGetOne(
    'invoices',
    { id: record?.invoiceId },
    { enabled: !!record?.invoiceId }
  );

  const handleSubmit = async (data: any) => {
    try {
      await dataProvider.update('crm/payments', {
        id: record.id,
        data: {
          amount: parseFloat(data.amount),
          paymentDate: data.paymentDate,
          paymentMethod: data.paymentMethod,
          notes: data.notes,
        },
        previousData: record,
      });

      notify('Payment updated successfully', { type: 'success' });
      refresh();
      onClose();
    } catch (error) {
      notify('Error updating payment', { type: 'error' });
    }
  };

  const handleDelete = async () => {
    try {
      await dataProvider.delete('crm/payments', { id: record.id });
      notify('Payment deleted successfully', { type: 'success' });
      refresh();
      onClose();
    } catch (error) {
      notify('Error deleting payment', { type: 'error' });
    }
  };

  if (!record) {return null;}

  const formatCurrency = (amount: number | undefined | null) => {
    const safeAmount = typeof amount === 'number' && !isNaN(amount) ? amount : 0;
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
    }).format(safeAmount);
  };

  return (
    <>
      <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
        <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          Edit Payment
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent>
          {/* Read-only context information */}
          <Box sx={{ mb: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
            <Typography variant="h6" gutterBottom>
              Payment Context
            </Typography>
            <Grid container spacing={2}>
              {customer && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <PersonIcon color="primary" fontSize="small" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Customer
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        {customer.displayName}
                      </Typography>
                      {customer.email && (
                        <Typography variant="body2" color="text.secondary">
                          {customer.email}
                        </Typography>
                      )}
                    </Box>
                  </Box>
                </Grid>
              )}
              {invoice && (
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <ReceiptIcon color="primary" fontSize="small" />
                    <Box>
                      <Typography variant="body2" color="text.secondary">
                        Invoice
                      </Typography>
                      <Typography variant="body1" fontWeight="medium">
                        #{invoice.invoiceNumber}
                      </Typography>
                      <Typography variant="body2" color="text.secondary">
                        Total: {formatCurrency(invoice.totalAmount)}
                      </Typography>
                    </Box>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Box>

          <Divider sx={{ mb: 2 }} />

          <SimpleForm
            onSubmit={handleSubmit}
            record={record}
            toolbar={<PaymentEditToolbar onDelete={() => setDeleteConfirmOpen(true)} />}
          >
            <Grid container spacing={3} sx={{ mt: 1 }}>
              <Grid item xs={12} md={6}>
                <NumberInput
                  source="amount"
                  label="Payment Amount ($)"
                  validate={[required()]}
                  fullWidth
                  min={0}
                  step={0.01}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <DateInput
                  source="paymentDate"
                  label="Payment Date"
                  validate={[required()]}
                  fullWidth
                  parse={(value) => {
                    if (!value) {return null;}
                    const date = new Date(value + (value.includes('T') ? '' : 'T00:00:00'));
                    return date.toISOString();
                  }}
                  format={(value) => {
                    if (!value) {return '';}
                    return value.includes('T') ? value.split('T')[0] : value;
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <SelectInput
                  source="paymentMethod"
                  label="Payment Method"
                  choices={[
                    { id: 'Cash', name: 'Cash' },
                    { id: 'Check', name: 'Check' },
                    { id: 'CreditCard', name: 'Credit Card' },
                    { id: 'BankTransfer', name: 'Bank Transfer' },
                    { id: 'PayPal', name: 'PayPal' },
                    { id: 'Other', name: 'Other' },
                  ]}
                  validate={[required()]}
                  fullWidth
                />
              </Grid>
              <Grid item xs={12}>
                <TextInput
                  source="notes"
                  label="Notes"
                  multiline
                  rows={3}
                  fullWidth
                />
              </Grid>
            </Grid>
          </SimpleForm>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog
        open={deleteConfirmOpen}
        onClose={() => setDeleteConfirmOpen(false)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete this payment? This action cannot be undone.
          </Typography>
        </DialogContent>
        <Box sx={{ p: 2, display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
          <Button onClick={() => setDeleteConfirmOpen(false)}>Cancel</Button>
          <Button onClick={handleDelete} color="error" variant="contained">
            Delete
          </Button>
        </Box>
      </Dialog>
    </>
  );
}; 